import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { User } from "lucide-react";
import { ProfileModalProps, useProfileData } from "@/lib/profile/profileData";
import GeneralProfileTab from "./GeneralProfileTab";
import AvailabilityProfileTab from "./AvailabilityProfileTab";
import AccreditionsProfileTab from "./AccreditationsProfileTab";
import ChangePassowordProfileTab from "./ChangePasswordProfileTab";
import DigitalCertificatesProfileTab from "./DigitalCertificatesProfileTab";
import { ErrorModal } from "@/components/ErrorModal";
import { DeleteConfirmationModal } from "@/components/DeleteConfirmationModal";

export default function ProfileModal({
  open,
  onOpenChange,
  isDarkMode = false,
}: ProfileModalProps) {
  const {
    tabs,
    activeTab,
    setActiveTab,
    errorMessage,
    errorModalOpen,
    setErrorModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteMessage,
    deleteAction,
    setErrorMessage,
    setDeleteMessage,
    setDeleteAction,
  } = useProfileData({ open });

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return <GeneralProfileTab />;
      case "availability":
        return <AvailabilityProfileTab />;
      case "accreditations":
        return <AccreditionsProfileTab />;
      case "signature":
        return (
          <DigitalCertificatesProfileTab
            open={open}
            activeTab={activeTab}
            setDeleteModalOpen={setDeleteModalOpen}
            setErrorModalOpen={setErrorModalOpen}
            setErrorMessage={setErrorMessage}
            setDeleteAction={setDeleteAction}
            setDeleteMessage={setDeleteMessage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-6xl h-[90vh] overflow-hidden transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            <User className="w-5 h-5" />
            My Profile
          </DialogTitle>
        </DialogHeader>

        <div className="flex h-[calc(90vh-120px)] min-h-[600px]">
          {/* Sidebar */}
          <div
            className={`w-64 border-r p-4 flex-shrink-0 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors duration-300 ${
                      activeTab === tab.id
                        ? "bg-slate-900 text-white"
                        : isDarkMode
                        ? "text-gray-300 hover:bg-gray-700"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div
            className={`flex-1 overflow-y-auto p-6 min-h-0 transition-colors duration-300 ${
              isDarkMode ? "bg-gray-900" : "bg-white"
            }`}
          >
            {renderTabContent()}
          </div>
        </div>
      </DialogContent>
      <ErrorModal
        open={errorModalOpen}
        onOpenChange={setErrorModalOpen}
        title="Error Creating Certificate"
        message={errorMessage}
        isDarkMode={isDarkMode}
      />
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={deleteAction}
        title="Delete Confirmation"
        message={deleteMessage}
        isDarkMode={isDarkMode}
      />
    </Dialog>
  );
}
